import React from "react";
// Vee Exception Report page

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomDataTable from "../../components/table/CustomTable";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import VeeExceptionsReportPieChart from "../charts/VeeExceptionsReportPieChart";

const allData = [
  {
    exceptionid: "EX00" + 1,
    meterid: "123456789",
    customerid: "987654321",
    datetime: "2024-05-07 08:15	",
    exceptiontype: "Missing Data",
    originalvalue: "N/A",
    estimatedvalue: 350,
    correctionstatus: "Pending Review",
    severitylevel: "High",
    operatornotes: "Check for communication issues",
  },
  {
    exceptionid: "EX00" + 2,
    meterid: "987654321",
    customerid: "123456789",
    datetime: "2024-05-08 09:20",
    exceptiontype: "Over Threshold",
    originalvalue: 500,
    estimatedvalue: 480,
    correctionstatus: "Corrected",
    severitylevel: "Medium",
    operatornotes: "Adjusted for peak usage",
  },
  {
    exceptionid: "EX00" + 3,
    meterid: "456789123",
    customerid: "654987321",
    datetime: "2024-05-09 10:30",
    exceptiontype: "Under Threshold",
    originalvalue: 200,
    estimatedvalue: 0,
    correctionstatus: "Pending Review",
    severitylevel: "Low",
    operatornotes: "Verification needed",
  },
  {
    exceptionid: "EX00" + 4,
    meterid: "123456789",
    customerid: "987654321",
    datetime: "2024-05-07 08:15	",
    exceptiontype: "Missing Data",
    originalvalue: null,
    estimatedvalue: 350,
    correctionstatus: "Pending Review",
    severitylevel: "High",
    operatornotes: "Check for communication issues",
  },
];

const VeeExceptionReport: React.FC = () => {
  const [selectedEventType, setSelectedEventType] = useState({
    label: "All",
    value: "All",
  });
  const [filteredData, setFilteredData] = useState(allData);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const downloadCSV = () => {
    const headers = Object.keys(filteredData[0]).join(",");
    const rows = filteredData
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const options = [
    { label: "All", value: "All" },
    { label: "Missing Data", value: "Missing Data" },
    { label: "Over Threhold", value: "Over Threhold" },
    { label: "Under Threhold", value: "Under Threhold" },
  ];

  const handleEventTypeChange = (event: any) => {
    setSelectedEventType(event);

    setFilteredData(
      event.value === "All"
        ? allData
        : allData?.filter((item) => item.exceptiontype === event.value)
    );
  };

  const COLUMNS = [
    "Exception Id",
    "Meter Id",
    "Customer Id",
    "Date & Time",
    "Exception Type",
    "Original Value",
    "Estimated Value",
    "Correction Status",
    "Severity Level",
    "Operator Notes",
  ];

  const rowsContent = filteredData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.exceptionid || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.meterid || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.customerid || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.datetime || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.exceptiontype || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.originalvalue || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.estimatedvalue || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.correctionstatus || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.severitylevel || "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.operatornotes || "-"}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card className="meters_title_card">
        <Typography variant="h5">VEE Exceptions Report</Typography>
      </Card>

      <Card sx={{ mt: "20px", maxHeight: "400px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Filter By Exception Type</Typography>
              <CustomOutlinedSelect
                placeholder={"Select Status"}
                options={options}
                width="fit-content"
                value={selectedEventType}
                onChange={(newValue) => handleEventTypeChange(newValue)}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <VeeExceptionsReportPieChart data={filteredData} />
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Report Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={downloadCSV}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent sx={{ overflow: "auto", maxHeight: "600px" }}>
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={filteredData?.length}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default VeeExceptionReport;
